<script setup lang="ts">
    import EchoButton from '@/js/components/EchoButton.vue';
    import EchoTooltip from '@/js/components/EchoTooltip.vue';
    import IconButton from '@/js/components/IconButton.vue';
    import TextareaInput from '@/js/components/TextareaInput.vue';
    import { useElementFocus } from '@/js/composables/useElementFocus';
    import {
        useMutationDeleteProtocolComment,
        useMutationPatchProtocolComment,
        useMutationPostProtocolCommentForQuestion,
        useMutationPostProtocolCommentForSection,
        useQueryGetProtocol,
    } from '@/js/services/api/protocol/protocolQueries';
    import { useGetMyAccount } from '@/js/hooks/user';
    import { Role, usePermissions } from '@/js/composables/usePermissions';
    import { useProtocolLock } from '@/js/composables/useProtocolLock';
    import { useConfirmationDialog } from '@/js/stores/confirmationDialog';
    import { __ } from '@/js/stores/expressions';
    import { dateToString } from '@/js/utils/dateUtils';
    import { computed, ref, watch } from 'vue';
    import { CheckIcon, MessageSquarePlusIcon, PencilIcon, TrashIcon, XIcon } from '@lucide/vue';

    const props = defineProps<{
        protocolId: string;
        protocolSectionId?: string;
        protocolQuestionAnswerId?: string;
        disabled: boolean;
    }>();

    const emit = defineEmits<{
        (e: 'update:commentsCount', count: number): void;
    }>();

    const { data: accountData } = useGetMyAccount();
    const { hasRole } = usePermissions();

    const showNewCommentForm = ref(false);
    const newComment = ref('');
    const editComment = ref('');
    const editCommentId = ref<string | null>(null);

    const { setFocus } = useElementFocus();

    const { mutateAsync: postCommentForSection, isPending: isPostingCommentForSection } =
        useMutationPostProtocolCommentForSection(props.protocolId);

    const { mutateAsync: postCommentForQuestion, isPending: isPostingCommentForQuestion } =
        useMutationPostProtocolCommentForQuestion(props.protocolId);

    const { mutateAsync: patchComment, isPending: isPatchingComment } =
        useMutationPatchProtocolComment(props.protocolId);

    const { mutateAsync: deleteComment } = useMutationDeleteProtocolComment(props.protocolId);

    const { data: protocolData } = useQueryGetProtocol(props.protocolId);

    const { lockReason } = useProtocolLock(() => protocolData.value?.protocol);

    const comments = computed(() => {
        const protocol = protocolData.value?.protocol;

        const answerComments =
            protocol?.sections.flatMap((section) => {
                return section.answers.flatMap((answer) => answer.comments || []) || [];
            }) || [];

        const sectionComments =
            protocol?.sections.flatMap((section) => section.comments || []) || [];

        return [...answerComments, ...sectionComments];
    });

    const submitComment = () => {
        if (props.protocolSectionId) {
            postCommentForSection({
                protocolSectionId: props.protocolSectionId,
                text: newComment.value,
            }).then(() => {
                showNewCommentForm.value = false;
                newComment.value = '';
            });
        } else if (props.protocolQuestionAnswerId) {
            postCommentForQuestion({
                protocolQuestionAnswerId: props.protocolQuestionAnswerId,
                text: newComment.value,
            }).then(() => {
                showNewCommentForm.value = false;
                newComment.value = '';
            });
        }
    };

    const saveComment = () => {
        if (editCommentId.value) {
            patchComment({
                commentId: editCommentId.value,
                text: editComment.value,
            }).then(() => {
                editCommentId.value = null;
                editComment.value = '';
            });
        }
    };

    const filteredComments = computed(() => {
        if (props.protocolSectionId) {
            return comments.value.filter(
                (comment) => comment.protocolSectionId === props.protocolSectionId
            );
        } else if (props.protocolQuestionAnswerId) {
            return comments.value.filter(
                (comment) => comment.protocolQuestionAnswerId === props.protocolQuestionAnswerId
            );
        } else {
            return [];
        }
    });

    const newCommentTextareaId = computed(() => {
        if (props.protocolSectionId) {
            return `new-comment-section-${props.protocolSectionId}`;
        } else if (props.protocolQuestionAnswerId) {
            return `new-comment-question-${props.protocolQuestionAnswerId}`;
        } else {
            return 'new-comment';
        }
    });

    const borderClass = computed(() => {
        return props.protocolQuestionAnswerId ? 'border-white' : 'border-gray-lightest';
    });

    const commentClass = computed(() => {
        return 'border-b-4 pb-2 mb-2 ' + borderClass.value;
    });

    const isPostingNewComment = computed(() => {
        return isPostingCommentForSection.value || isPostingCommentForQuestion.value;
    });

    const deleteCommentConfirmation = (commentId: string) => {
        const confirm = useConfirmationDialog();

        confirm
            .openDialog({
                title: __('delete-comment-confirmation'),
                message: '',
                confirmText: __('delete-comment'),
            })
            .then(async (confirmed: boolean) => {
                if (confirmed) {
                    deleteComment(commentId);
                }
            });
    };

    const createNewComment = () => {
        showNewCommentForm.value = true;
        setFocus('#' + newCommentTextareaId.value);
    };

    const editExistingComment = (commentId: string) => {
        editCommentId.value = commentId;
        showNewCommentForm.value = false;
        setFocus('#' + `edit-comment-${editCommentId.value}`);
    };

    watch(
        () => props.disabled,
        () => {
            if (props.disabled) {
                showNewCommentForm.value = false;
                newComment.value = '';
                editCommentId.value = null;
                editComment.value = '';
            }
        }
    );

    watch(
        () => editCommentId.value,
        () => {
            const comment = filteredComments.value?.find((c) => c.id === editCommentId.value);
            editComment.value = comment ? comment.text : '';
        }
    );

    watch(
        () => filteredComments.value.length,
        (value) => {
            emit('update:commentsCount', value);
        },
        { immediate: true }
    );
</script>

<template>
    <ul class="text-body" :class="filteredComments?.length ? 'px-12' : ''">
        <li v-for="comment in filteredComments" :key="comment.id" :class="commentClass">
            <article v-if="editCommentId !== comment.id">
                <div class="flex items-center">
                    <h5 class="text-body-bold">
                        {{ comment.createdByName }},
                        <time :datetime="comment.createdAt">
                            {{ dateToString(new Date(comment.createdAt)) }}
                        </time>
                    </h5>

                    <nav
                        v-if="
                            comment.createdByUserId === accountData?.id ||
                            hasRole(Role.ADMIN) ||
                            hasRole(Role.SYSOP)
                        "
                    >
                        <EchoTooltip
                            :label="lockReason ?? ''"
                            :disabled="!lockReason"
                            :tabindex="lockReason ? 0 : undefined"
                        >
                            <ul class="flex border-l ml-4" :class="borderClass">
                                <li v-if="comment.createdByUserId === accountData?.id">
                                    <IconButton
                                        size="size-5"
                                        area-size="w-8 h-7"
                                        :tooltip="__('edit-comment')"
                                        :disabled="
                                            !!editCommentId ||
                                            showNewCommentForm ||
                                            disabled ||
                                            !!lockReason
                                        "
                                        @click="editExistingComment(comment.id)"
                                    >
                                        <PencilIcon class="size-5" />
                                    </IconButton>
                                </li>
                                <li>
                                    <IconButton
                                        size="size-5"
                                        area-size="w-8 h-7"
                                        :tooltip="__('delete-comment')"
                                        :disabled="
                                            !!editCommentId ||
                                            showNewCommentForm ||
                                            disabled ||
                                            !!lockReason
                                        "
                                        @click="deleteCommentConfirmation(comment.id)"
                                    >
                                        <TrashIcon class="size-5" />
                                    </IconButton>
                                </li>
                            </ul>
                        </EchoTooltip>
                    </nav>
                </div>
                <p class="text-body">
                    {{ comment.text }}
                </p>
            </article>
            <form
                v-else-if="editCommentId === comment.id"
                class="flex flex-col gap-2"
                @submit.prevent="saveComment()"
            >
                <p class="text-body-bold">
                    {{ $userInfo?.given_name }} {{ $userInfo?.family_name }}
                </p>
                <TextareaInput :id="`edit-comment-${editCommentId}`" v-model="editComment" />
                <div class="flex gap-2">
                    <EchoButton
                        type="submit"
                        variant="primary"
                        size="small"
                        :loading="isPatchingComment"
                        :disabled="!editComment"
                    >
                        {{ __('save') }}
                        <template #icon-right><CheckIcon /></template>
                    </EchoButton>
                    <EchoButton variant="secondary" size="small" @click="editCommentId = ''">
                        {{ __('cancel') }}
                        <template #icon-right><XIcon /></template>
                    </EchoButton>
                </div>
            </form>
        </li>
    </ul>

    <template v-if="protocolSectionId || protocolQuestionAnswerId">
        <div v-if="!showNewCommentForm" :class="filteredComments?.length ? 'px-12' : ''">
            <EchoTooltip
                :label="lockReason ?? ''"
                :disabled="!lockReason"
                :tabindex="lockReason ? 0 : undefined"
            >
                <EchoButton
                    variant="tertiary"
                    :disabled="!!editCommentId || disabled || !!lockReason"
                    @click="createNewComment()"
                >
                    <template #icon-left><MessageSquarePlusIcon /></template>
                    {{ __('add-comment') }}
                </EchoButton>
            </EchoTooltip>
        </div>

        <form
            v-else
            :class="filteredComments?.length ? 'px-12' : ''"
            class="flex flex-col gap-2"
            @submit.prevent="submitComment()"
        >
            <p class="text-body-bold">{{ $userInfo?.given_name }} {{ $userInfo?.family_name }}</p>
            <TextareaInput :id="newCommentTextareaId" v-model="newComment" />
            <div class="flex gap-2">
                <EchoButton
                    type="submit"
                    variant="primary"
                    size="small"
                    :loading="isPostingNewComment"
                    :disabled="!newComment"
                >
                    {{ __('save') }}
                    <template #icon-right><CheckIcon /></template>
                </EchoButton>
                <EchoButton
                    variant="secondary"
                    size="small"
                    @click="
                        showNewCommentForm = false;
                        newComment = '';
                    "
                >
                    {{ __('cancel') }}
                    <template #icon-right><XIcon /></template>
                </EchoButton>
            </div>
        </form>
    </template>
</template>
